import { resolveContentLink } from "@/lib/contentLink";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Target, Users, Building2 } from "lucide-react";
import { GlassyImage } from "@/components/GlassyImage";
import { ClientLogos } from "@/components/ClientLogos";
import { Badge } from "@/components/ui/badge";
import { ScheduleDemo, FAQ } from "@/components/sections";
import { PageResources } from "@/components/sections/PageResources";
import { PageContainer } from "@/components/layouts/MainLayout";
import { useLocalizedImage, useLocalizedImages } from "@/hooks/use-localized-image";
import { T, THtml } from "@/components/editable/T";
import { getLocalizedRoutePath } from "@/lib/routeUtils";
import { FeatureImageFrame } from "@/components/FeatureImageFrame";
import { ModuleCards } from "@/components/sections/ModuleCards";

import heroImageFallback from "@/assets/illustrations/devgoals-hero.webp";
import row1Image from "@/assets/illustrations/devgoals-row1-evaluatie-doelen.png";
import row2Image from "@/assets/illustrations/devgoals-row2-smart-ai.webp";
import row3Image from "@/assets/illustrations/devgoals-row3-1on1-followup.avif";
import row4Image from "@/assets/illustrations/devgoals-row4-org-goals.avif";
import customerSuccessImage from "@/assets/images/customer-success-team-nieuw.webp";

const PAGE = "developmentGoals";
const featureColors = ["#F3EDFD", "#FDF2ED", "#FFFBEB", "#FAEDFD"];

const DevelopmentGoals = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'nl';
  const demoPath = getLocalizedRoutePath('demo', lang);

  const heroImg = useLocalizedImage('developmentGoals', 'hero', 'hero-image', heroImageFallback, { preload: true });
  const csImg = useLocalizedImage('developmentGoals', 'customerSuccess', 'cs-image', customerSuccessImage);
  const featureImages = useLocalizedImages('developmentGoals', 'features',
    ['row1-image', 'row2-image', 'row3-image', 'row4-image'],
    { 'row1-image': row1Image, 'row2-image': row2Image, 'row3-image': row3Image, 'row4-image': row4Image }
  );

  const features = [
    { titleKey: "features.row1.title", titleDefault: "Maak van evaluaties het startpunt van ontwikkeling", bodyKey: "features.row1.body", bodyDefault: "Een goed evaluatiegesprek stopt niet bij terugkijken. Met Learned vertaal je inzichten uit evaluaties direct naar concrete ontwikkeldoelen, zodat ontwikkeling een logisch vervolg krijgt.", imageKey: 'row1-image', color: featureColors[0], demoCta: true },
    { titleKey: "features.row2.title", titleDefault: "Stel sneller SMART-doelen op met AI", bodyKey: "features.row2.body", bodyDefault: "Geef leidinggevenden en medewerkers een sterke eerste aanzet voor doelen die specifiek, meetbaar en haalbaar zijn. Zo voorkom je vage ambities en maak je ontwikkeling concreet.", imageKey: 'row2-image', color: featureColors[1] },
    { titleKey: "features.row3.title", titleDefault: "Houd voortgang zichtbaar in 1:1's", bodyKey: "features.row3.body", bodyDefault: "Bespreek doelen niet één keer, maar volg ze doorlopend op in 1:1 gesprekken. Zo blijft er aandacht voor voortgang, obstakels en de ondersteuning die iemand nodig heeft om verder te komen.", imageKey: 'row3-image', color: featureColors[2], cta: { labelKey: "features.row3.cta", labelDefault: "Ontdek 1:1 gesprekken", link: resolveContentLink(`/${lang}/features/1-1-gesprekken`, lang) } },
    { titleKey: "features.row4.title", titleDefault: "Verbind persoonlijke groei aan organisatiedoelen", bodyKey: "features.row4.body", bodyDefault: "Koppel ontwikkeldoelen en KPI's aan team- en organisatiedoelen. Zo zien medewerkers hoe hun groei bijdraagt aan het grotere geheel en krijgt HR meer grip op ontwikkeling in de organisatie.", imageKey: 'row4-image', color: featureColors[3] },
  ];


  return (
    <>
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to={`/${lang}/`} className="hover:text-primary transition-colors">
              <T page={PAGE} k="breadcrumb.home" d="Home" />
            </Link>
            <span>›</span>
            <Link to={`/${lang}/platform/performance-management/`} className="hover:text-primary transition-colors">
              <T page={PAGE} k="breadcrumb.parent" d="Performance Management" />
            </Link>
            <span>›</span>
            <span style={{ color: "#2A2334" }}>
              <T page={PAGE} k="breadcrumb.current" d="Ontwikkelplannen" />
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <T page={PAGE} k="hero.subtitle" d="Ontwikkelplannen" as="p" className="text-lead text-muted-foreground mb-4" />
              <T page={PAGE} k="hero.title" d="Van evaluatie naar een ontwikkelplan dat wordt opgevolgd" as="h1" className="mb-6" style={{ color: '#2A2334' }} />
              <THtml page={PAGE} k="hero.body" d="Help medewerkers verder groeien met duidelijke, haalbare doelen. Learned ondersteunt leidinggevenden met AI-suggesties en geeft medewerkers zicht op de stappen die nodig zijn om echt vooruit te komen." as="div" className="text-lead text-muted-foreground mb-8 max-w-xl" />
              <div className="flex flex-col sm:flex-row gap-4">
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => window.open('https://app.learned.io/register', '_blank')}>
                  <T page={PAGE} k="hero.tryFree" d="Probeer gratis" />
                </Button>
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <T page={PAGE} k="hero.planDemo" d="Plan demo" />
                </Button>
              </div>
            </div>
            <div className={`animate-fade-in relative transition-opacity duration-300 ${heroImg.loading ? 'opacity-0' : 'opacity-100'}`} style={{ animationDelay: '200ms' }}>
              <GlassyImage src={heroImg.imageUrl || heroImageFallback} alt={heroImg.altText || "Ontwikkeldoelen"} className="w-full h-[450px] object-cover" />
              <div className="absolute -right-4 bottom-12 bg-white rounded-2xl shadow-xl ring-[8px] ring-white/80 p-4 transform rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:block opacity-0 animate-fade-float-in" style={{ animationDelay: '800ms', animationFillMode: 'forwards' }}>
                <div className="flex items-center gap-2 mb-3 bg-[#9055EE] text-white rounded-full px-4 py-2 text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  <T page={PAGE} k="hero.cards.createGoal" d="Maak doel" />
                </div>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2.5 text-sm text-gray-700">
                    <div className="w-8 h-8 rounded-lg bg-[#FDF2ED] flex items-center justify-center"><Target className="w-4 h-4 text-orange-400" /></div>
                    <T page={PAGE} k="hero.cards.personal" d="Persoonlijk" />
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-gray-700">
                    <div className="w-8 h-8 rounded-lg bg-[#F3EDFD] flex items-center justify-center"><Users className="w-4 h-4 text-[#9055EE]" /></div>
                    <T page={PAGE} k="hero.cards.team" d="Team" />
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-gray-700">
                    <div className="w-8 h-8 rounded-lg bg-[#FAEDFD] flex items-center justify-center"><Building2 className="w-4 h-4 text-pink-500" /></div>
                    <T page={PAGE} k="hero.cards.organisation" d="Organisatie" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ClientLogos />
        </PageContainer>
      </section>

      <section className="pt-[60px] pb-20 px-6">
        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div key={index} className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <T page={PAGE} k={feature.titleKey} d={feature.titleDefault} as="h2" className="feature-section-heading text-foreground" />
                    <THtml page={PAGE} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />
                    {(feature.cta || feature.demoCta) && (
                      <Button asChild className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white">
                        <Link to={demoPath}>
                          <T page={PAGE} k="features.demoCta" d="Plan een demo" />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    )}
                  </div>
                  <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    <FeatureImageFrame color={feature.color} src={featureImages[feature.imageKey]?.imageUrl || ''} alt={featureImages[feature.imageKey]?.altText || ''} />
                  </div>
                </div>
              );
            })}
          </div>
        </PageContainer>
      </section>

      <section className="py-20 px-6">
        <PageContainer>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 rounded-2xl overflow-hidden aspect-square">
              <img src={csImg.imageUrl || customerSuccessImage} alt={csImg.altText || "Customer Success"} className="w-full h-full object-cover object-[72%_top] md:object-center rounded-2xl" />
            </div>
            <div className="order-1">
              <T page={PAGE} k="customerSuccess.label" d="Customer Success" as="p" className="text-sm font-semibold text-[#9055EE] mb-2" />
              <h2 className="feature-section-heading mb-4 text-foreground"><T page={PAGE} k="customerSuccess.title" d="Begeleiding bij doelen die echt in beweging komen" /></h2>
              <T page={PAGE} k="customerSuccess.body" d="Onze specialisten helpen leidinggevenden om betere doelen te formuleren én op te volgen in de praktijk. Zo wordt ontwikkelen geen losse stap, maar een vast onderdeel van je performance proces." as="p" className="text-lead text-muted-foreground mb-6" />
              <Button className="rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white" onClick={() => navigate(`/${lang}/customer-success/`)}>
                <T page={PAGE} k="customerSuccess.cta" d="Ontdek onze services" />
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </PageContainer>
      </section>

      <ModuleCards
        translationPage={PAGE}
        titleKey="nextStepsTitle"
        titleDefault="Waar het ontwikkelplan verder op aansluit"
        introKey="nextStepsIntro"
        introDefault="Een ontwikkelplan volgt uit het carrièrepad en de vlootschouw, en krijgt opvolging in de Performance Management cyclus."
        cards={[
          {
            routeKey: "careerPaths",
            titleKey: "nextSteps.card1.title",
            titleDefault: "Carrièrepaden",
            bodyKey: "nextSteps.card1.body",
            bodyDefault: "De skillgap per rol maakt duidelijk waar een ontwikkelplan over moet gaan.",
          },
          {
            routeKey: "talentReview",
            titleKey: "nextSteps.card2.title",
            titleDefault: "Vlootschouw",
            bodyKey: "nextSteps.card2.body",
            bodyDefault: "Afspraken uit de talent review landen als ontwikkelplan bij de medewerker.",
          },
          {
            routeKey: "performanceManagement",
            titleKey: "nextSteps.card3.title",
            titleDefault: "Performance Management",
            bodyKey: "nextSteps.card3.body",
            bodyDefault: "Ontwikkelen en evalueren binnen één proces, tegen dezelfde verwachtingen.",
          },
        ]}
      />

      <ScheduleDemo />

      <FAQ dark headingClassName="feature-section-heading" className="mx-0 mb-[-96px] pb-[192px] rounded-none" style={{ background: 'linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)' }} page="developmentGoals"
        titleKey="developmentGoals.faq.title" titleDefault="Veelgestelde vragen" subtitleKey="developmentGoals.faq.subtitle" subtitleDefault="Heb je nog vragen?"
        items={[
          { questionKey: "developmentGoals.faq.q1.question", questionDefault: "Hoe helpen ontwikkeldoelen medewerkers echt verder?", answerKey: "developmentGoals.faq.q1.answer", answerDefault: "Ontwikkeldoelen maken groei concreet. In plaats van algemene ambities leg je vast waar iemand aan werkt, waarom dat belangrijk is en welke voortgang daarbij hoort." },
          { questionKey: "developmentGoals.faq.q2.question", questionDefault: "Hoe stel je goede ontwikkeldoelen op?", answerKey: "developmentGoals.faq.q2.answer", answerDefault: "Goede ontwikkeldoelen zijn duidelijk, relevant en haalbaar. Learned helpt leidinggevenden en medewerkers om doelen concreter te formuleren, zodat ontwikkeling niet vaag blijft maar echt richting krijgt." },
          { questionKey: "developmentGoals.faq.q3.question", questionDefault: "Kun je ontwikkeldoelen koppelen aan evaluaties?", answerKey: "developmentGoals.faq.q3.answer", answerDefault: "Ja, dat is juist de kracht. Inzichten uit evaluaties kun je direct vertalen naar nieuwe ontwikkeldoelen, zodat gesprekken niet eindigen bij terugkijken maar leiden tot een volgende stap." },
          { questionKey: "developmentGoals.faq.q4.question", questionDefault: "Hoe houd je zicht op voortgang?", answerKey: "developmentGoals.faq.q4.answer", answerDefault: "Voortgang wordt zichtbaar doordat doelen doorlopend terugkomen in 1:1 gesprekken en evaluaties. Zo blijft ontwikkeling onderdeel van het werk, in plaats van iets dat tussendoor uit beeld verdwijnt." },
        ]}
      />
    </>
  );
};

export default DevelopmentGoals;
